import React, { createContext, useContext, useState, useRef, useEffect } from 'react'

// Create music player context for controlling PositionalAudio
const MusicPlayerContext = createContext()

export function MusicPlayerProvider({ children, initialAudioEnabled = true }) {
  // Global audio safety scaling - reduces all audio output to prevent hearing damage
  const GLOBAL_AUDIO_SCALE = 0.2 // 20% of original volume for safety
  
  const [currentTrack, setCurrentTrack] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.5) // Now 50% which will be scaled to 10% actual volume
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  
  // Autoplay state management
  const [hasAutoplayStarted, setHasAutoplayStarted] = useState(false)
  const [userHasPaused, setUserHasPaused] = useState(false)
  const [lastKnownPosition, setLastKnownPosition] = useState(0)
  const [isUserInside, setIsUserInside] = useState(false)
  
  // Ref to HTML5 audio only
  const audioRef = useRef()

  // Music library
  const musicLibrary = [
    {
      id: "track_1",
      title: "That Rainy Day",
      artist: "Wendy Marcini",
      description: "A beautiful jazz standard performed by the talented Wendy Marcini.",
      albumCover: "/graphics/album/graphic-album-thatRainyDay.webp",
      audioFile: "/audio/ES_That Rainy Day - Wendy Marcini.mp3"
    },
    {
      id: "track_2",
      title: "Malvas Polska",
      artist: "Sandra Marteleur",
      description: "A lovely Swedish folk tune performed by Sandra Marteleur.",
      albumCover: "/graphics/album/graphic-album-droemmaren.webp",
      audioFile: "/audio/ES_Malvas Polska - Sandra Marteleur.mp3"
    },
    {
      id: "track_3",
      title: "New Shoes",
      artist: "Golden Age Radio",
      description: "An upbeat track with vintage charm from Golden Age Radio.",
      albumCover: "/graphics/album/graphic-album-newShoes.webp",
      audioFile: "/audio/ES_New Shoes - Golden Age Radio.mp3"
    },
    {
      id: "track_4",
      title: "Sing It Loud",
      artist: "Golden Age Radio",
      description: "An energetic and joyful song from Golden Age Radio.",
      albumCover: "/graphics/album/graphic-album-singItLoud.webp",
      audioFile: "/audio/ES_Sing It Loud - Golden Age Radio.mp3"
    },
    {
      id: "track_5",
      title: "Solskenvals",
      artist: "Sandra Marteleur",
      description: "A beautiful waltz that captures the warmth of sunshine, performed by Sandra Marteleur.",
      albumCover: "/graphics/album/graphic-album-solskensvals.webp",
      audioFile: "/audio/ES_Solskenvals - Sandra Marteleur.mp3"
    }
  ]

  // My Music library - personal/custom tracks
  const myMusicLibrary = [
    {
      id: "my_track_1",
      title: "Dirt Track",
      artist: "Björn Grauers & James Newcastle",
      description: "My first single release as Björn Grauers, a collaboration and mixed by my good friend Janne Hako.",
      albumCover: "/graphics/album/album-dirttrack.webp",
      audioFile: "/audio/song-dirttrack-web.mp3"
    }
  ]



  // Initialize with first track
  useEffect(() => {
    if (musicLibrary.length > 0 && !currentTrack) {
      setCurrentTrack(musicLibrary[0])
    }
  }, [])

  // Register HTML5 audio ref
  const registerAudio = (htmlAudioRef) => {
    audioRef.current = htmlAudioRef
    // Set initial volume when audio is registered (apply global scaling)
    if (htmlAudioRef) {
      htmlAudioRef.volume = volume * GLOBAL_AUDIO_SCALE
    }
    console.log('HTML5 audio registered with MusicPlayer, scaled volume set to:', volume * GLOBAL_AUDIO_SCALE)
  }

  // Control functions
  const togglePlayPause = async () => {
    if (!audioRef.current) return
    
    const audio = audioRef.current
    
    if (isPlaying) {
      audio.pause()
      // Track that user manually paused
      setUserHasPaused(true)
      setLastKnownPosition(audio.currentTime)
    } else {
      try {
        // Check if audio is ready to play
        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA
          await audio.play()
          setUserHasPaused(false)
        } else {
          // Wait for audio to be ready
          console.log('Audio not ready, waiting...')
          const waitForReady = () => {
            return new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                reject(new Error('Audio load timeout'))
              }, 5000)
              
              const onCanPlay = () => {
                clearTimeout(timeout)
                audio.removeEventListener('canplay', onCanPlay)
                resolve()
              }
              
              audio.addEventListener('canplay', onCanPlay)
            })
          }
          
          await waitForReady()
          await audio.play()
          setUserHasPaused(false)
        }
      } catch (error) {
        console.error('Audio play failed:', error)
        // Reset playing state if play fails
        setIsPlaying(false)
      }
    }
  }

  const changeVolume = (newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume))
    setVolume(clampedVolume)
    if (audioRef.current) {
      // Apply global scaling to actual audio volume
      audioRef.current.volume = clampedVolume * GLOBAL_AUDIO_SCALE
    }
  }

  const changeTrack = async (trackIndex, autoPlay = false) => {
    if (trackIndex < 0 || trackIndex >= musicLibrary.length) return
    
    const wasPlaying = isPlaying
    
    // Stop current audio and reset state
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
    }
    
    setCurrentTrack(musicLibrary[trackIndex])
    setCurrentTime(0)
    setDuration(0)
    
    // For gallery clicks, we need to ensure audio is ready before playing
    if (autoPlay || wasPlaying) {
      // Wait for audio element to be ready
      setTimeout(async () => {
        if (audioRef.current) {
          try {
            // Try to load the audio first
            audioRef.current.load()
            
            // Wait for it to be ready to play
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioRef.current.removeEventListener('canplay', onCanPlay)
                audioRef.current.removeEventListener('error', onError)
                resolve()
              }
              
              const onError = (error) => {
                audioRef.current.removeEventListener('canplay', onCanPlay)
                audioRef.current.removeEventListener('error', onError)
                reject(error)
              }
              
              audioRef.current.addEventListener('canplay', onCanPlay)
              audioRef.current.addEventListener('error', onError)
            })
            
            // Now try to play
            await audioRef.current.play()
            setIsPlaying(true)
            
          } catch (error) {
            console.warn('Failed to play track after change:', error)
            setIsPlaying(false)
          }
        }
      }, 100)
    }
  }

  const nextTrack = (autoPlay = false) => {
    // Combine both libraries for seamless auto-play
    const allTracks = [...musicLibrary, ...myMusicLibrary]
    const currentIndex = allTracks.findIndex(track => track.id === currentTrack?.id)
    const nextIndex = (currentIndex + 1) % allTracks.length
    const nextTrack = allTracks[nextIndex]
    
    // Determine if it's from main library or my music
    const isFromMyMusic = myMusicLibrary.some(track => track.id === nextTrack.id)
    
    if (isFromMyMusic) {
      const myMusicIndex = myMusicLibrary.findIndex(track => track.id === nextTrack.id)
      playMyMusicTrack(myMusicIndex, autoPlay)
    } else {
      const mainLibraryIndex = musicLibrary.findIndex(track => track.id === nextTrack.id)
      changeTrack(mainLibraryIndex, autoPlay)
    }
  }

  const previousTrack = () => {
    // Combine both libraries for seamless navigation
    const allTracks = [...musicLibrary, ...myMusicLibrary]
    const currentIndex = allTracks.findIndex(track => track.id === currentTrack?.id)
    const prevIndex = currentIndex === 0 ? allTracks.length - 1 : currentIndex - 1
    const prevTrack = allTracks[prevIndex]
    
    // Determine if it's from main library or my music
    const isFromMyMusic = myMusicLibrary.some(track => track.id === prevTrack.id)
    
    if (isFromMyMusic) {
      const myMusicIndex = myMusicLibrary.findIndex(track => track.id === prevTrack.id)
      playMyMusicTrack(myMusicIndex)
    } else {
      const mainLibraryIndex = musicLibrary.findIndex(track => track.id === prevTrack.id)
      changeTrack(mainLibraryIndex)
    }
  }

  // Seek to a specific time (0-1 ratio)
  const seekTo = (ratio) => {
    if (!audioRef.current || !duration) return
    
    const audio = audioRef.current
    const newTime = ratio * duration
    
    // Check if the audio is sufficiently loaded for seeking
    if (audio.readyState >= 2) {
      try {
        audio.currentTime = newTime
        setCurrentTime(newTime)
        
        // If audio was playing and stops after seeking, try to resume
        if (isPlaying && audio.paused) {
          setTimeout(() => {
            audio.play().catch(e => {
              console.log('Resume after seek failed:', e)
              setIsPlaying(false)
            })
          }, 100)
        }
      } catch (error) {
        console.error('Seek failed:', error)
      }
    } else {
      console.log('Audio not ready for seeking')
    }
  }

  // Play track from My Music library
  const playMyMusicTrack = async (trackIndex, autoPlay = true) => {
    if (trackIndex < 0 || trackIndex >= myMusicLibrary.length) return
    
    const wasPlaying = isPlaying
    
    // Stop current audio and reset state
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
    }
    
    setCurrentTrack(myMusicLibrary[trackIndex])
    setCurrentTime(0)
    setDuration(0)
    
    // For my music, default to autoplay when clicked from gallery
    if (autoPlay || wasPlaying) {
      setTimeout(async () => {
        if (audioRef.current) {
          try {
            // Load and prepare audio
            audioRef.current.load()
            
            // Wait for audio to be ready
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioRef.current.removeEventListener('canplay', onCanPlay)
                audioRef.current.removeEventListener('error', onError)
                resolve()
              }
              
              const onError = (error) => {
                audioRef.current.removeEventListener('canplay', onCanPlay)
                audioRef.current.removeEventListener('error', onError)
                reject(error)
              }
              
              audioRef.current.addEventListener('canplay', onCanPlay)
              audioRef.current.addEventListener('error', onError)
            })
            
            // Now play
            await audioRef.current.play()
            setIsPlaying(true)
            
          } catch (error) {
            console.warn('Failed to play my music track:', error)
            setIsPlaying(false)
          }
        }
      }, 100)
    }
  }

  // Track user location (inside/outside house)
  const setUserLocation = (isInside) => {
    const wasInside = isUserInside
    setIsUserInside(isInside)
    
    console.log(`User location: ${isInside ? 'inside' : 'outside'}, was: ${wasInside ? 'inside' : 'outside'}`)
    
    // If user just entered the house from outside
    if (isInside && !wasInside) {
      handleEnterHouse()
    }
    // If user left the house
    else if (!isInside && wasInside) {
      handleLeaveHouse()
    }
  }

  // Handle entering the house
  const handleEnterHouse = () => {
    console.log('User entered house')
    
    // Only autoplay if:
    // 1. User chose audio on loading screen, AND
    // 2. Music hasn't started yet, OR music was playing when they left (not manually paused)
    const shouldAutoplay = initialAudioEnabled && (!hasAutoplayStarted || (!userHasPaused && hasAutoplayStarted))
    
    if (shouldAutoplay && musicLibrary.length > 0 && audioRef.current) {
      console.log('Starting/resuming music autoplay')
      
      // If this is the first time, start from beginning
      if (!hasAutoplayStarted) {
        setCurrentTrack(musicLibrary[0])
        setCurrentTime(0)
        setHasAutoplayStarted(true)
      } 
      // If resuming, restore position
      else if (currentTrack && lastKnownPosition > 0) {
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.currentTime = lastKnownPosition
          }
        }, 100)
      }
      
      // Start playback
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(e => console.log('Autoplay failed:', e))
        }
      }, 500)
    }
  }

  // Handle leaving the house
  const handleLeaveHouse = () => {
    console.log('User left house')
    
    if (audioRef.current && isPlaying) {
      // Save current position and pause
      setLastKnownPosition(audioRef.current.currentTime)
      audioRef.current.pause()
      // Don't set userHasPaused=true here, since this is automatic pause
    }
  }

  // Reset autoplay state (for debugging or manual reset)
  const resetAutoplayState = () => {
    setHasAutoplayStarted(false)
    setUserHasPaused(false)
    setLastKnownPosition(0)
    console.log('Autoplay state reset')
  }





  const value = {
    // State
    currentTrack,
    isPlaying,
    volume,
    currentTime,
    duration,
    musicLibrary,
    myMusicLibrary,
    hasAutoplayStarted,
    userHasPaused,
    isUserInside,
    GLOBAL_AUDIO_SCALE, // Export for use by other audio components
    
    // Actions
    registerAudio,
    togglePlayPause,
    changeVolume,
    changeTrack,
    nextTrack,
    previousTrack,
    seekTo,
    playMyMusicTrack,
    setUserLocation,
    resetAutoplayState,
    setCurrentTime,
    setDuration,
    setIsPlaying
  }

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  )
}

export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext)
  if (!context) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider')
  }
  return context
}